import React, { useState } from "react";
import { useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { useAuth, isFamily, isTrainer, isRestricted } from "../contexts/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role) {
    // Restricted users (family + trainer) don't touch the dashboard — send them to their player list.
    const to = isRestricted(user) ? "/my-players" : (location.state?.from || "/");
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const me = await login(email.trim(), password);
      toast.success("Signed in");
      const to = isRestricted(me) ? "/my-players" : (location.state?.from || "/");
      navigate(to, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" style={{ background: "#0a0a0a" }}>
      {/* Tech backdrop: grid + ambient glows + orbit ring */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "40px 40px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)", WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)" }} />
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(216,184,84,0.18), transparent 60%)", filter: "blur(20px)" }} />
      <div className="absolute -bottom-48 -right-32 w-[620px] h-[620px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(58,160,255,0.16), transparent 60%)", filter: "blur(24px)" }} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.05] pointer-events-none" style={{ width: 760, height: 760 }} />
      <div className="absolute left-1/2 top-6 -translate-x-1/2 font-heading uppercase whitespace-nowrap text-white/[0.03] leading-none pointer-events-none select-none" style={{ fontSize: "clamp(5rem, 18vw, 16rem)" }}>Maximize</div>

      <div className="relative w-full max-w-md" data-testid="login-page" style={{ animation: "loginIn .6s cubic-bezier(.16,1,.3,1) both" }}>
        <style>{`@keyframes loginIn { from { opacity: 0; transform: translateY(14px) } to { opacity: 1; transform: none } }`}</style>
        <div className="text-center mb-8">
          <img src={EMBLEM_URL} alt="Maximize Basketball" className="h-16 w-auto mx-auto mb-5 drop-shadow-[0_0_24px_rgba(216,184,84,0.35)]" />
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.5em] text-[var(--org-accent)] mb-2">
            <span className="w-8 h-px bg-[var(--org-accent)]" /> Coach Portal <span className="w-8 h-px bg-[var(--org-accent)]" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-tight leading-none">
            Maximize Analytics
          </h1>
          <div className="mt-3 text-xs uppercase tracking-widest text-[#a0a0a0]">Sign in to continue</div>
        </div>

        <form onSubmit={submit} className="relative border border-white/10 bg-[#14141a]/85 backdrop-blur-2xl p-7 space-y-5" style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.03), 0 40px 80px -30px rgba(0,0,0,0.9), 0 0 60px -20px rgba(58,160,255,0.15)" }}>
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #d8b854 30%, #3aa0ff 70%, transparent)" }} />
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 bg-[#0a0a0a] border border-white/10 text-white text-sm focus:outline-none focus:border-[#3aa0ff]"
              required
              autoFocus
              autoComplete="email"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1.5 block">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-3 bg-[#0a0a0a] border border-white/10 text-white text-sm focus:outline-none focus:border-[#3aa0ff]"
              required
              autoComplete="current-password"
              data-testid="login-password"
            />
          </div>

          {error && (
            <div className="text-xs text-red-400 border border-red-400/30 bg-red-400/5 px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 h-11 bg-[var(--org-accent)] text-[#121212] text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] hover:-translate-y-px disabled:opacity-60"
            data-testid="login-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            Sign in
          </button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-[11px] uppercase tracking-[0.3em] text-[#8a8a8a] hover:text-[var(--org-accent)]"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-[#575757]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] shadow-[0_0_8px_#22c55e]" /> Secure · Invite-only access
        </div>
        <div className="mt-4 flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.3em]" data-testid="login-legal-links">
          <Link to="/privacy" className="text-[#a0a0a0] hover:text-[var(--org-accent)] underline underline-offset-4 decoration-white/20" data-testid="login-privacy-link">Privacy Policy</Link>
          <span className="w-px h-3 bg-white/20" />
          <Link to="/terms" className="text-[#a0a0a0] hover:text-[var(--org-accent)] underline underline-offset-4 decoration-white/20" data-testid="login-terms-link">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
}

const EMBLEM_URL = "https://customer-assets-agu9un31.emergentagent.net/job_player-analytics-73/artifacts/4wc3ngyr_MBDG-Emblem-OnDark.png.webp";
